import { DeleteLinkButton, OverviewHeader } from "components";
import { AddToPacksButton } from "components/buttons/AddToPacksButton/AddToPacksButton";
import { DuplicateButton } from "components/buttons/linkButtons/headerButtons/DuplicateButton";
import { ToggleVisibilityButton } from "components/buttons/linkButtons/toggleVisibilityButton/ToggleVisibilityButton";
import { Tooltip } from "components/common/tooltip";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { RolePermissions, Strings } from "enums";
import Restricted from "features/Application/Restricted";
import useToggle from "hooks/useToggle";
import { Icon } from "semantic-ui-react";
import styles from "./ExternalLinksOverviewHeader.module.scss";
import TagsManageContainer from "./TagsManageContainer";
import { CreateExternalLinkButton } from "./CreateExternalLinkButton";

export interface ExternalLinksOverviewHeaderProps {
  selectedIds: number[];
  purchasedSelected: boolean;
  draftSelected: boolean;
  hiddenSelected: boolean;
  visibleSelected: boolean;
  unpublishedSelected: boolean;
  clearSelection: () => void;
  handleDuplicate: (ids: number[]) => void;
  handleDelete: (selectedIds: number[], flowsCount?: number, packsCount?: number) => void;
  handleToggleVisibility: (selectedIds: number[], hidden: boolean) => () => void;
}

export const ExternalLinksOverviewHeader = (props: ExternalLinksOverviewHeaderProps) => {
  const {
    purchasedSelected,
    draftSelected,
    selectedIds,
    clearSelection,
    handleDuplicate,
    handleDelete,
    handleToggleVisibility,
    hiddenSelected,
    visibleSelected,
    unpublishedSelected,
  } = props;

  const [packsModalShown, togglePackModal] = useToggle();
  const message = Strings.tooltipMessages.purchased.operationsDisabled("assets");

  const renderBatchButtons = () => {
    const hiddenAndShownSelected = hiddenSelected && visibleSelected;

    return (
      <>
        <Restricted permissions={[RolePermissions.AssetsCreate]}>
          <ToggleVisibilityButton
            onClick={handleToggleVisibility(selectedIds, hiddenSelected)}
            hidden={hiddenSelected}
            isDisabled={hiddenAndShownSelected || unpublishedSelected}
          />
        </Restricted>
        <Restricted permissions={[RolePermissions.PacksManage, RolePermissions.AssetsCreate]}>
          <AddToPacksButton onClick={togglePackModal} isDisabled={purchasedSelected || draftSelected} />
        </Restricted>
        {
          <Restricted permissions={[RolePermissions.AssetsCreate]}>
            <DuplicateButton
              entityName="assets"
              purchasedSelected={purchasedSelected}
              onClick={() => handleDuplicate(selectedIds)}
            />
          </Restricted>
        }
        {
          <Restricted permissions={[RolePermissions.AssetsCreate]}>
            <Tooltip
              hideOnScroll
              content={message}
              position="left center"
              showAlways={purchasedSelected}
              target={
                <DeleteLinkButton
                  after={purchasedSelected && <Icon size="large" className="fa fa-exclamation-circle" />}
                  isDisabled={purchasedSelected}
                  onClick={() => handleDelete(selectedIds)}
                />
              }
            />
          </Restricted>
        }
        <Restricted permissions={[RolePermissions.AssetsCreate]}>
          <TagsManageContainer
            entity="assets"
            isDisabled={purchasedSelected}
            selectedIds={selectedIds}
            onComplete={clearSelection}
          />
        </Restricted>
        <AddAssetsToPackModal
          showModal={packsModalShown}
          onClose={togglePackModal}
          onComplete={clearSelection}
          selectedItemIds={selectedIds}
          contentType="Link"
        />
      </>
    );
  };

  return (
    <OverviewHeader title="Links" itemName="Link" titleForGA="Assets - Links" selectedItemsAmount={selectedIds.length}>
      <div className={styles.root}>{selectedIds.length ? renderBatchButtons() : <CreateExternalLinkButton />}</div>
    </OverviewHeader>
  );
};
